import type { DashboardStory } from '@src/types.ts/story';

const toMs = (iso?: string): number => {
  if (!iso) return 0;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? 0 : ms;
};

/**
 * Newest timestamp across a story record *and* every clip under it — what the
 * "Last activity" column and the Recent Activity sort both read.
 *
 * A story's own `_publishedAt` is Dato's `meta.published_at`, which only moves
 * when the *story record* is written: ingest, rename, a clip added or removed,
 * a saved article / social post. Editing a clip in Creator Studio writes the
 * *video* record instead (`VideoRepository`), leaving the story untouched — so
 * on story `_publishedAt` alone, a story someone edited all morning sorts below
 * one that was merely uploaded yesterday and never opened. Folding the clips in
 * is what makes the value mean what the column says.
 *
 * Returns 0 when nothing carries a timestamp, so callers can compare directly.
 */
export function getStoryLastActivityMs(story: DashboardStory): number {
  let newest = toMs(story._publishedAt);
  for (const clip of story.otherVideos ?? []) {
    newest = Math.max(newest, toMs(clip?._updatedAt), toMs(clip?._publishedAt));
  }
  return newest;
}

/** ISO form of {@link getStoryLastActivityMs}; `undefined` when unknown. */
export function getStoryLastActivityIso(
  story: DashboardStory,
): string | undefined {
  const ms = getStoryLastActivityMs(story);
  return ms ? new Date(ms).toISOString() : undefined;
}

/**
 * When the story was uploaded — Dato's record creation, which never moves after
 * ingest. The stable counterpart to {@link getStoryLastActivityMs}: it answers
 * "what's new here?" where activity answers "what's being worked on?".
 */
export function getStoryUploadedMs(story: DashboardStory): number {
  return toMs(story._createdAt);
}
